import { expectArray, expectBoolean, expectNullable, expectNumber, expectOptionalString, expectRecord, expectString } from "./schema";
export const AI_USE_CASES = [
    "splitJapaneseStudentName",
    "suggestFamilyAccountDisplayName",
    "recitalOrderDraft",
    "danceStepSuggestion",
    "publicEventTextDraft"
];
export const AI_RESPONSE_MIME_TYPES = ["text/plain", "application/json"];
export const AI_PROVIDER_TYPES = ["vertexGemini", "geminiDeveloperApi", "memory"];
export const FEATURE_ENTITLEMENT_KEYS = [
    "aiAssistant",
    "aiFamilyAccountNameSuggestion",
    "billingReports",
    "billingManagement"
];
export const FEATURE_ENTITLEMENT_SOURCES = ["manual", "plan", "trial", "system"];
export const AI_USAGE_EVENT_STATUSES = ["success", "failed", "fallback"];
export const FAMILY_ACCOUNT_NAME_SUGGESTION_CONFIDENCES = ["high", "medium", "low"];
function parseStringEnum(value, allowedValues, path) {
    const parsed = expectString(value, path);
    if (!allowedValues.includes(parsed)) {
        throw new Error(`${path} must be one of: ${allowedValues.join(", ")}`);
    }
    return parsed;
}
function parseAttachment(value, path) {
    const record = expectRecord(value, path);
    return {
        attachmentId: expectOptionalString(record.attachmentId, `${path}.attachmentId`),
        fileName: expectString(record.fileName, `${path}.fileName`),
        mimeType: expectString(record.mimeType, `${path}.mimeType`),
        gcsUri: expectOptionalString(record.gcsUri, `${path}.gcsUri`),
        signedUrl: expectOptionalString(record.signedUrl, `${path}.signedUrl`),
        inlineBase64: expectOptionalString(record.inlineBase64, `${path}.inlineBase64`),
        fileSizeBytes: record.fileSizeBytes === undefined ? null : expectNumber(record.fileSizeBytes, `${path}.fileSizeBytes`)
    };
}
function parseSafetyProfile(value, path) {
    const record = expectRecord(value, path);
    return {
        profileId: expectOptionalString(record.profileId, `${path}.profileId`),
        allowInlineBase64: record.allowInlineBase64 === undefined
            ? false
            : expectBoolean(record.allowInlineBase64, `${path}.allowInlineBase64`),
        maxAttachmentBytes: record.maxAttachmentBytes === undefined
            ? null
            : expectNumber(record.maxAttachmentBytes, `${path}.maxAttachmentBytes`)
    };
}
function parseTokenUsage(value, path) {
    const record = expectRecord(value, path);
    return {
        inputTokens: record.inputTokens === undefined ? null : expectNumber(record.inputTokens, `${path}.inputTokens`),
        outputTokens: record.outputTokens === undefined ? null : expectNumber(record.outputTokens, `${path}.outputTokens`),
        totalTokens: record.totalTokens === undefined ? null : expectNumber(record.totalTokens, `${path}.totalTokens`)
    };
}
function parseFeatureEntitlementLimits(value, path) {
    const record = expectRecord(value, path);
    return {
        monthlyRequests: record.monthlyRequests === undefined ? null : expectNumber(record.monthlyRequests, `${path}.monthlyRequests`),
        monthlyTokens: record.monthlyTokens === undefined ? null : expectNumber(record.monthlyTokens, `${path}.monthlyTokens`),
        maxAttachmentBytes: record.maxAttachmentBytes === undefined
            ? null
            : expectNumber(record.maxAttachmentBytes, `${path}.maxAttachmentBytes`)
    };
}
function parseExistingFamilyAccountOption(value, path) {
    const record = expectRecord(value, path);
    return {
        familyAccountId: expectString(record.familyAccountId, `${path}.familyAccountId`),
        displayName: expectString(record.displayName, `${path}.displayName`)
    };
}
export function parseAiUseCase(value, path = "aiUseCase") {
    return parseStringEnum(value, AI_USE_CASES, path);
}
export function parseAiResponseMimeType(value, path = "aiResponseMimeType") {
    return parseStringEnum(value, AI_RESPONSE_MIME_TYPES, path);
}
export function parseAiProviderType(value, path = "aiProviderType") {
    return parseStringEnum(value, AI_PROVIDER_TYPES, path);
}
export function parseFeatureEntitlementKey(value, path = "featureEntitlementKey") {
    return parseStringEnum(value, FEATURE_ENTITLEMENT_KEYS, path);
}
export function parseFeatureEntitlementSource(value, path = "featureEntitlementSource") {
    return parseStringEnum(value, FEATURE_ENTITLEMENT_SOURCES, path);
}
export function parseAiUsageEventStatus(value, path = "aiUsageEventStatus") {
    return parseStringEnum(value, AI_USAGE_EVENT_STATUSES, path);
}
export function parseFamilyAccountNameSuggestionConfidence(value, path = "familyAccountNameSuggestionConfidence") {
    return parseStringEnum(value, FAMILY_ACCOUNT_NAME_SUGGESTION_CONFIDENCES, path);
}
export function parseAiGenerateRequest(value, path = "aiGenerateRequest") {
    const record = expectRecord(value, path);
    return {
        useCase: parseAiUseCase(record.useCase, `${path}.useCase`),
        providerConfigId: expectOptionalString(record.providerConfigId, `${path}.providerConfigId`),
        model: expectOptionalString(record.model, `${path}.model`),
        prompt: expectString(record.prompt, `${path}.prompt`),
        systemInstruction: expectOptionalString(record.systemInstruction, `${path}.systemInstruction`),
        responseMimeType: parseAiResponseMimeType(record.responseMimeType, `${path}.responseMimeType`),
        responseSchema: expectNullable(record.responseSchema, (input) => expectRecord(input, `${path}.responseSchema`), `${path}.responseSchema`),
        attachments: record.attachments ? expectArray(record.attachments, parseAttachment, `${path}.attachments`) : [],
        safetyProfile: expectNullable(record.safetyProfile, parseSafetyProfile, `${path}.safetyProfile`),
        metadata: expectNullable(record.metadata, (input) => expectRecord(input, `${path}.metadata`), `${path}.metadata`)
    };
}
export function parseAiGenerateResponse(value, path = "aiGenerateResponse") {
    const record = expectRecord(value, path);
    return {
        provider: parseAiProviderType(record.provider, `${path}.provider`),
        model: expectString(record.model, `${path}.model`),
        text: expectString(record.text, `${path}.text`),
        mimeType: parseAiResponseMimeType(record.mimeType, `${path}.mimeType`),
        tokenUsage: expectNullable(record.tokenUsage, parseTokenUsage, `${path}.tokenUsage`)
    };
}
export function parseFeatureEntitlement(value, path = "featureEntitlement") {
    const record = expectRecord(value, path);
    return {
        tenantId: expectString(record.tenantId, `${path}.tenantId`),
        featureKey: parseFeatureEntitlementKey(record.featureKey, `${path}.featureKey`),
        enabled: expectBoolean(record.enabled, `${path}.enabled`),
        planId: expectOptionalString(record.planId, `${path}.planId`),
        source: parseFeatureEntitlementSource(record.source, `${path}.source`),
        limits: expectNullable(record.limits, parseFeatureEntitlementLimits, `${path}.limits`),
        validFrom: expectOptionalString(record.validFrom, `${path}.validFrom`),
        validTo: expectOptionalString(record.validTo, `${path}.validTo`),
        createdAt: expectString(record.createdAt, `${path}.createdAt`),
        updatedAt: expectString(record.updatedAt, `${path}.updatedAt`),
        updatedByPlatformUserId: expectOptionalString(record.updatedByPlatformUserId, `${path}.updatedByPlatformUserId`)
    };
}
export function parseSuggestFamilyAccountNameRequest(value, path = "suggestFamilyAccountNameRequest") {
    const record = expectRecord(value, path);
    return {
        studentDisplayName: expectString(record.studentDisplayName, `${path}.studentDisplayName`),
        existingFamilyAccounts: record.existingFamilyAccounts
            ? expectArray(record.existingFamilyAccounts, parseExistingFamilyAccountOption, `${path}.existingFamilyAccounts`)
            : []
    };
}
export function parseSuggestFamilyAccountNameResponse(value, path = "suggestFamilyAccountNameResponse") {
    const record = expectRecord(value, path);
    const givenName = record.givenName;
    if (typeof givenName !== "string") {
        throw new Error(`${path}.givenName must be a string`);
    }
    return {
        familyName: expectString(record.familyName, `${path}.familyName`),
        givenName,
        confidence: parseFamilyAccountNameSuggestionConfidence(record.confidence, `${path}.confidence`),
        reason: expectString(record.reason, `${path}.reason`),
        suggestedFamilyAccountDisplayName: expectString(record.suggestedFamilyAccountDisplayName, `${path}.suggestedFamilyAccountDisplayName`),
        needsHumanReview: expectBoolean(record.needsHumanReview, `${path}.needsHumanReview`),
        source: parseStringEnum(record.source, ["ai", "fallback"], `${path}.source`),
        featureEnabled: expectBoolean(record.featureEnabled, `${path}.featureEnabled`)
    };
}
